// Cart types and localStorage helpers — client-side only.
// Cart stores EUR amounts; conversion to display/Stripe currency happens
// at render/checkout time via src/lib/fx.ts.

export interface CartItem {
  sku: string;
  name: string;
  shortName: string;
  /** Canonical price — EUR amount. */
  priceEUR: number;
  image?: string;
  quantity: number;
}

export interface CartState {
  items: CartItem[];
}

// v2: cart switched from priceTRY (with stale, incorrect rates) to canonical
// priceEUR. Bumping the key invalidates old carts so they don't display or
// charge nonsense values.
const CART_KEY = "bpderma_cart_v2";
const LEGACY_CART_KEY = "bpderma_cart";

export function loadCart(): CartState {
  if (typeof window === "undefined") return { items: [] };
  try {
    // One-shot: clear the legacy v1 cart so old TRY-amount items don't linger.
    if (localStorage.getItem(LEGACY_CART_KEY)) {
      localStorage.removeItem(LEGACY_CART_KEY);
    }
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return { items: [] };
    return JSON.parse(raw) as CartState;
  } catch {
    return { items: [] };
  }
}

export function saveCart(state: CartState): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(CART_KEY, JSON.stringify(state));
}

export function addItemToCart(state: CartState, item: Omit<CartItem, "quantity">): CartState {
  const existing = state.items.find((i) => i.sku === item.sku);
  if (existing) {
    return {
      items: state.items.map((i) =>
        i.sku === item.sku ? { ...i, quantity: i.quantity + 1 } : i
      ),
    };
  }
  return { items: [...state.items, { ...item, quantity: 1 }] };
}

export function removeItemFromCart(state: CartState, sku: string): CartState {
  return { items: state.items.filter((i) => i.sku !== sku) };
}

export function updateItemQuantity(state: CartState, sku: string, quantity: number): CartState {
  if (quantity <= 0) return removeItemFromCart(state, sku);
  return {
    items: state.items.map((i) => (i.sku === sku ? { ...i, quantity } : i)),
  };
}

/** Cart subtotal in EUR. */
export function cartTotalEur(state: CartState): number {
  return state.items.reduce((sum, i) => sum + i.priceEUR * i.quantity, 0);
}

export function cartCount(state: CartState): number {
  return state.items.reduce((sum, i) => sum + i.quantity, 0);
}
